import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Card from "components/card";
import { Form, Input, Select, message } from "antd";
import FormItem from "components/fields/FormItem";
import { requiredField } from "utils/crudSagaHelper";
import TextArea from "antd/es/input/TextArea";
import CrudButtons from "components/button/crudbuttons";
import Button from "components/button";
import {
    getTicketDetails,
    doTicketCreate,
} from "../../../../../../redux/ticket/ticketState";
import {
    getClientTeamList
} from "../../../../../../redux/optionlist/optionlistState";
import { DEPARTMENT_ICONS, validateImageAttachment } from "variables/constants";
import { Send, Paperclip, X } from "lucide-react";

const MessageTicket = ({ onCancel, ...props }) => {
    const dispatch = useDispatch();

    const authUser = useSelector((state) => state.auth.user);

    const recordData = useSelector((state) => state.ticket.ticketDetails?.data);
    const departmentList = useSelector((state) => state.optionlist.clientTeamList?.items || []);

    const { recordId, mode } = props;
    const [form] = Form.useForm();
    const fileInputRef = useRef(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [attachment, setAttachment] = useState(null); 
    
    useEffect(() => {
        dispatch(getTicketDetails({ tic_id: recordId }));
    }, [dispatch, recordId]);

    useEffect(() => {
        if (authUser) {
            dispatch(
                getClientTeamList({
                    clientId: authUser.client_id ?? 0,
                })
            );
        }
    }, [dispatch, authUser]);

    useEffect(() => {
        if (!selectedDepartment) {
            setCategoryOptions([]);
            return;
        }

        const filtered = (recordData?.categories || []).filter(
            cat => cat.queue_id === selectedDepartment
        );

        if (filtered.length > 0) {
            setCategoryOptions(filtered);
        } else {
            setCategoryOptions([
                {
                    value: 0,
                    label: "Others",
                },
            ]);
        }
    }, [selectedDepartment, recordData?.categories]);

    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const result = await validateImageAttachment(file, { maxSizeMB: 20, maxWidth: 8000, maxHeight: 8000 });
            if (!result.valid) {
                message.error(result.error);
                e.target.value = '';
                return;
            }
            setAttachment(file);
        }
        e.target.value = '';
    };

    const removeAttachment = () => setAttachment(null);

    const onSubmit = (formData) => {
        try {
            const payload = new FormData();
            payload.append('tic_department_id', formData.tic_department_id);
            payload.append('tic_category_id', formData.tic_category_id || '');
            payload.append('tic_priority_id', formData.tic_priority_id || '');
            payload.append('tic_title', formData.tic_title || '');
            payload.append('tic_description', formData.tic_description || '');
            payload.append('is_message', 0);
            
            if (attachment) {
                payload.append('attachment', attachment);
            }

            dispatch(doTicketCreate(payload));

        } catch (error) {
            console.error('Save failed:', error);
        }
    };

    const priorityLabelMap = {
        Low: "Low - General inquiry",
        Medium: "Medium - Standard request",
        High: "High - Urgent issue",
    };

    const modifiedPriorityOptions = (recordData?.priority || []).map(item => ({
        ...item,
        label: priorityLabelMap[item.label] || item.label,
    }));

    const formatDepartmentOptions = (departments) => {
        if (!departments || !Array.isArray(departments)) return [];
        
        return departments.map(dept => {
            const IconComponent = DEPARTMENT_ICONS[dept.department_name];
            return {
                value: dept.department_id,
                label: (
                    <div className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="w-4 h-4 text-slate-600" />}
                        <span>{dept.label}</span>
                    </div>
                ),
            };
        });
    };

    const departmentOptions = formatDepartmentOptions(departmentList);
    


    return (
        <Card className="p-0">
            <Form
                form={form}
                className={mode !== "view" ? "form" : ""}
                layout="vertical" 
                onFinish={onSubmit}
            >
                <div className="space-y-2">
                    <div className="space-y-2">
                        <FormItem
                            label="Department"
                            name="tic_department_id"
                            rules={[requiredField()]}
                        >
                            <Select
                                id="tic_department_id"    
                                placeholder="Select department"
                                options={departmentOptions}
                                showLabel={true}
                                allowClear={true}
                                className="input-field"
                                onChange={(value) => {
                                    setSelectedDepartment(value);
                                    form.setFieldValue('tic_category_id', undefined);
                                }}
                            />
                        </FormItem>
                        <FormItem
                            name="tic_category_id" 
                            label="Category"
                            rules={[requiredField()]}
                        >
                            <Select 
                                id="tic_category_id"
                                allowClear={true}
                                className="input-field"
                                options={categoryOptions}
                                placeholder="Select category"
                            />
                        </FormItem>
                        <FormItem
                            name="tic_priority_id" 
                            label="Priority"
                            rules={[requiredField()]}
                        >
                            <Select id="tic_priority_id"
                                allowClear={true}
                                className="input-field"
                                options={modifiedPriorityOptions}
                                placeholder="Select priority"
                            />
                        </FormItem>
                        <FormItem
                            name="tic_title" 
                            label="Subject"
                            rules={[requiredField()]}
                        >
                            <Input id="tic_title"
                                allowClear={true}
                                className="input-field"
                                placeholder="Brief description of your inquiry"
                            />
                        </FormItem>
                        <FormItem
                            name="tic_description"
                            label="Details"
                            rules={[requiredField()]}
                        >
                            <TextArea
                                id="tic_description" 
                                className="input-field"
                                maxLength={255}
                                placeholder="Provide details about your request..."
                                rows={3}
                            />
                        </FormItem>
                        <div className="flex items-center gap-2 pt-2">
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                onChange={handleFileSelect} 
                                accept="image/*" 
                                style={{ display: 'none' }} 
                            />
                            <Button 
                                type="button" 
                                onClick={handleAttachClick} 
                                className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all shrink-0 border bg-white text-slate-900 h-8 rounded-lg px-3 gap-2"
                            >
                                <Paperclip className="w-4 h-4" /> Attach File
                            </Button>
                            {attachment && (
                                <div className="flex items-center gap-2 text-[11px] p-1.5 rounded-md text-slate-600 bg-slate-50">
                                    {attachment.name}
                                    <button type="button" 
                                        onClick={removeAttachment} 
                                        className="text-red-400 hover:text-red-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <CrudButtons
                            mode={mode}
                            recordId={recordData?.tic_id ?? 0}
                            isModal={true}
                            modalId={recordData?.tic_id > 0 ? "edit_ticket" : "add_ticket"}
                            doHideModal={onCancel}
                            cancelLabel="Cancel"
                            submitLabel="Send Message"
                            moduleId={0}
                            cancelIcon=""
                            submitIcon={<Send className="w-4 h-4 mr-2" />}
                            submitClassName="ticket submit-ticket emerald"
                            cancelClassName="ticket cancel-ticket"
                        />
                    </div>
                </div>
            </Form>
        </Card>
    )
};

export default MessageTicket;